import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  Device,
  TransformationService,
  SchedulerFormat,
  LmapService,
  TestAssembleService,
  TestServerConfigService,
  TestMode,
  AGENT_MODEL
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TruespeedConfigFieldMapping } from './tabs/models/model';

@Injectable({
  providedIn: 'root'
})
export class TruespeedSetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  exceedsIPRange: boolean;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tabDataChangeEvents = new BehaviorSubject<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  streamDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  // eslint-disable-next-line
  testTaskName = 'truespeed';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  selectedTestType = {
    name: 'Truespeed',
    description: 'Monitor truespeed connections between two sites',
    id: 'truespeed'
  };
  monitorId = '';
  monitorType = 'on-going';
  tenantId = 'pass.local';
  defaultTcpPort = [{ name: 'First available', value: 0 }];
  tcpPorts = [];
  maxDownStream = 100;
  maxUpStream = 100;
  defaultBandWidth = 100;
  siteList = [];
  disableSteamInputs = true;
  controllerConfig = null;
  destinationIP = '';
  private readonly unsubscribe: Subject<void> = new Subject();
  private getControllerConfig = '/uicomm/getControllerConfiguration';
  licensePath = '/licensing/licenses';
  serverConfigPath = '/uicomm/getServerConfig';
  clientToAgentInfo = {
    licenseInfo: null,
    serverConfigInfo: null,
    dmzServerUrl: null,
    secondaryTestURL: null,
    dmzServerUrlAdvanced: null,
    secondaryTestURLAdvanced: null,
    activeLicense: 'tf',
    dmzURL: null,
    vtaURL: null
  };
  editConfigSrcAgentTypes = ['JMEP-10G', 'vTA', 'vSPA-SAT'];
  editConfigDestAgentTypes = ['JMEP-10G', 'vTA', 'vTA-TS', 'vSPA-SAT', 'vSPA-SAT-TS'];
  serverConfigDetails: BehaviorSubject<any> = new BehaviorSubject<any>([]);

  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  getControllerConfiguration(): void {
    const url = `${this.getControllerConfig}`;
    this.controllerConfig = this.apiService.post$<any>(url);
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }
  //eslint-disable-next-line
  setDestinationIP(ip: any): void {
    this.destinationIP = ip;
  }
  //eslint-disable-next-line
  getDestinationIP() {
    return this.destinationIP;
  }
  streamDataChanged(data: { key: string; value: number; valid: boolean }): void {
    if (data) {
      this.tabs.forEach(tab => {
        tab.output[data.key] = data.value;
      });
    }
    this.streamDataChangeEvents.next(data);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }

  setDestinationAgent(device: Device): void {
    this.selectedDestination = device;
  }

  setMaxDownStream(bandwidth: number | string): void {
    this.maxDownStream = Number(bandwidth);
  }

  setMaxUpStream(bandwidth: number | string): void {
    this.maxUpStream = Number(bandwidth);
  }

  setTCPPorts(ports: Array<string>): void {
    let tcpPorts = [];
    tcpPorts = Object.assign([], this.defaultTcpPort);
    ports.forEach(port => tcpPorts.push({ name: port, value: parseInt(port) }));
    this.tcpPorts = tcpPorts;
  }

  roundToOneDecimal(data: number): number {
    return data ? +data?.toFixed(1) : data;
  }

  //eslint-disable-next-line
  getAllTestParams(): any {
    if (!this.output[TruespeedConfigFieldMapping.SATURATION]) {
      this.output[TruespeedConfigFieldMapping.ENABLE_DYNAMIC_SATURATION] = null;
    }
    const configParams = this._getConfigParameters();
    this.output['truespeed-parameters'] = configParams;
    return this._getFormatOutput();
  }

  getEngineAParams(): any {
    if (this.output['engine-a-info']) {
      delete this.output['engine-a-info']['agent-type'];
      this.output['engine-a-info']['address'] = this.output?.['srcAssignedIpAddress'];
      this.output['engine-a-info']['test-ip'] = this.output?.['test-ip'];
      this.output['engine-a-info']['test-subnet-mask'] = this.output?.['test-subnet-mask'];
      this.output['engine-a-info']['test-gateway'] = this.output?.['test-gateway'];
      if (this.selectedOriginator.getModel() === AGENT_MODEL.JMEP_10G) {
        //MV-26918 for the JMEP address field need to send the Esam IP
        const srcEsamIp =
          this.output?.['selectedOriginator']?.['deviceData']?.['capabilities']?.['phy']?.[
            'management-ip'
          ];
        this.output['engine-a-info']['address'] =
          srcEsamIp || this.output?.['srcAssignedIpAddress'];
        this.output['engine-a-info']['tx-interface'] = this.output?.['tx-interface'];
        this.output['engine-a-info']['test-vlan-id'] = this.output?.['test-vlan-id'];
      }
    }
    return this.output['engine-a-info'];
  }

  getEngineBParams(): any {
    if (this.output['engine-b-info']) {
      delete this.output['engine-b-info']['agent-type'];
      this.output['engine-b-info']['address'] = this.output?.['destAssignedIpAddress'];
      this.output['engine-b-info']['test-ip'] = this.output?.['dest-test-ip'];
      this.output['engine-b-info']['test-subnet-mask'] = this.output?.['dest-test-subnet-mask'];
      this.output['engine-b-info']['test-gateway'] = this.output?.['dest-test-gateway'];
      if (this.selectedDestination.getModel() === AGENT_MODEL.JMEP_10G) {
        //MV-26918 for the JMEP address field need to send the Esam IP
        const destEsamIp =
          this.output?.['selectedDestination']?.['deviceData']?.['capabilities']?.['phy']?.[
            'management-ip'
          ];
        this.output['engine-b-info']['address'] =
          destEsamIp || this.output?.['destAssignedIpAddress'];
        this.output['engine-b-info']['tx-interface'] = this.output?.['dest-tx-interface'];
        this.output['engine-b-info']['test-vlan-id'] = this.output?.['dest-test-vlan-id'];
      }
    }
    return this.output['engine-b-info'];
  }

  //eslint-disable-next-line
  createLMAPObject(dataModel: any): any {
    const srcAgentType = this.selectedOriginator?.getModel() || null;
    const destinationAgentType = this.selectedDestination?.getModel() || null;
    if (
      this.output.testMode === TestMode.MA_TO_MA &&
      this.editConfigSrcAgentTypes.includes(srcAgentType) &&
      this.editConfigDestAgentTypes.includes(destinationAgentType)
    ) {
      if (dataModel['engine-a-info']) {
        dataModel['engine-a-info'] = this.getEngineAParams();
      }
      if (dataModel['engine-b-info']) {
        dataModel['engine-b-info'] = this.getEngineBParams();
      }
    }
    const testPointA = this.selectedOriginator?.deviceData;
    const testPointB = this.selectedDestination?.deviceData;
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    //const events = this.createEvents(testSchedule);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    //ATW-3448 - Make as ondemand test when repeat value is none
    if (!this.output.scheduler?.repeatInterval) {
      this.output.runType = 'on-demand';
    }
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }

  getScheduleTimeStamp(timestamp: string): string {
    if (timestamp.toLowerCase() === 'now') {
      return null; //To start the test immediately -Need to update based on backend value
    } else {
      const currentTimestamp = new Date().getTime();
      if (Number(timestamp) < currentTimestamp) {
        return null;
      } else {
        return new Date(Number(timestamp)).toISOString();
      }
    }
  }
  //eslint-disable-next-line
  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj: SchedulerFormat = { ...this.output.scheduler };
    // create the test schedule
    const testScheduleName = this.output.alias;
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.timeStamp && !schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['truespeed:truespeed'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };

    const actionOption2 = {
      id: 'role',
      value: 'primary'
    };

    const actionOption3 = {
      id: 'hide-client-results',
      value: 'false'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption2);
    if (this.output.testMode === TestMode.CLIENT_TO_MA) optionArray.push(actionOption3);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    const tags = this.getTagInfo();
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);

    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // rt results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'rt',
      collectorURL,
      schedObj.repeatInterval,
      schedObj.timeStamp
    );
    return lmapified;
  }

  getTagInfo(): Array<string> {
    const tags = [];
    if (this.output.scheduler?.repeatInterval) {
      tags.push('monitor-type:on-going');
      tags.push(
        'dst-agent-id:' +
          (this.selectedDestination?.getDevice().id
            ? this.selectedDestination?.getDevice().id
            : null)
      );
      return tags;
    } else {
      return null;
    }
  }

  //eslint-disable-next-line
  createControllerConfig(testPointA, testPointB, lmapified): any {
    let controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    if (this.output.testMode === TestMode.MA_TO_MA) {
      const secondaryConfig = JSON.parse(JSON.stringify(lmapified));
      let foundRole = false,
        foundPhyId = false;
      // secondary config is created as a copy of primary config, so need to update for role and phy-id
      for (const x in secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0]
        .option) {
        // update role to secondary
        if (
          !foundRole &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'role'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = 'secondary';
          foundRole = true;
        }
        // remove phy-id object if secondary device doesn't have multiple ports
        if (!foundPhyId && !testPointB.phyId) {
          if (
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x]
              .id === 'phy-id'
          ) {
            foundPhyId = true;
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.splice(
              x,
              1
            );
          }
        }

        // set the value of phy-id
        if (
          !foundPhyId &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'phy-id'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = testPointB.phyId ? testPointB.phyId : ''; // set value if exists, blank otherwise
          foundPhyId = true;
        }
        if (foundRole && foundPhyId) {
          break;
        }
      }

      // case when primary device is single port, so need to add phy-id object for secondary config
      if (!foundPhyId) {
        if (testPointB.phyId) {
          const optionObjIndex =
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.length;
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ] = {};
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].id = 'phy-id';
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].value = testPointB.phyId;
        }
      }
      controllerConfig = this.testAssembleService.addSecondaryTestConfig(
        controllerConfig,
        'secondaryConfig',
        testPointB,
        this.testTaskName,
        secondaryConfig
      );
    }

    controllerConfig.test['test-list-data'] = {};
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data'].mode = this.output.testMode;
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output.customer,
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    if (this.output.testMode === TestMode.CLIENT_TO_MA) {
      controllerConfig.test['controller'] = {
        'max-exec-count': this.output['max-execution-counts'],
        'expiration-date': this.output['expiration-date']
      };
      controllerConfig.test['extended-config']['testPoints'] = [
        {
          connections: [this.selectedOriginator.getDevice().connections[0]],
          id: this.selectedOriginator.getDevice().uniqueId,
          ip: this.selectedOriginator.getDevice().mgmtIPaddress,
          model: this.selectedOriginator.getModel(),
          name: this.selectedOriginator.getDevice().deviceName,
          port: this.output['engine-a-info'].port,
          selected: true,
          tag: 'Test Point A',
          testPortName: '',
          testPortPhyId: this.selectedOriginator.getDevice().phyId,
          type: 'inventory',
          vendor: this.selectedOriginator.getVendor().toLowerCase(),
          version: this.selectedOriginator.getVersion().value
        }
      ];
      controllerConfig['runlater'] = 'yes';
    } else if (this.output.testMode === TestMode.MA_TO_JMEP) {
      controllerConfig.test['controller'] = {
        'max-exec-count': this.output['max-execution-counts'],
        'expiration-date': this.output['expiration-date']
      };
    }
    if (this.output.testMode !== TestMode.CLIENT_TO_MA) {
      controllerConfig.test['extended-config']['testPoints'] = [
        {
          connections: [this.selectedOriginator.getDevice().connections[0]],
          id: this.selectedOriginator.getDevice().uniqueId,
          ip: this.selectedOriginator.getDevice().mgmtIPaddress,
          model: this.selectedOriginator.getModel(),
          name: this.selectedOriginator.getDevice().deviceName,
          port: this.output['engine-a-info']?.port,
          selected: true,
          tag: 'Test Point A',
          testPortName: '',
          testPortPhyId: this.selectedOriginator.getDevice()?.phyId,
          type: 'inventory',
          vendor: this.selectedOriginator.getVendor()?.toLowerCase(),
          version: this.selectedOriginator.getVersion()?.value
        }
      ];
      if (this.selectedDestination) {
        controllerConfig.test['extended-config']['testPoints'].push({
          connections: [this.selectedDestination.getDevice().connections[0]],
          id: this.selectedDestination.getDevice().uniqueId,
          ip: this.selectedDestination.getDevice().ipaddress,
          model: this.selectedDestination.getModel(),
          name: this.selectedDestination.getDevice().deviceName,
          port: this.output['engine-b-info']?.port,
          selected: true,
          tag: 'Test Point B',
          testPortName: '',
          testPortPhyId: this.selectedDestination.getDevice()?.phyId,
          type: 'inventory',
          vendor: this.selectedDestination.getVendor()?.toLowerCase(),
          version: this.selectedDestination.getVersion()?.value
        });
      }
    }
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output.customer,
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output.technician,
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output.alias,
      comments: this.output.comments
    };
    controllerConfig.test['extended-config']['test'] = {
      mode: this.output.testMode,
      nameOnProbe: 'truespeed',
      selected: true,
      testType: 'truespeed'
    };

    controllerConfig.test['ipv6'] = 'no';

    return controllerConfig;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  formatYangBaseModel(formControlName: any, formControlValue: any): any {
    const yangModel = {
      'truespeed:sequence': {
        configs: {
          'truespeed-parameters': {}
        }
      }
    };
    yangModel['truespeed:sequence'].configs['truespeed-parameters'][formControlName] =
      formControlValue;
    return yangModel;
  }

  // eslint-disable-next-line
  generateSecondaryTestURL(serverConfig: any, shaID: string): any {
    let port = null;
    let proxyIP = null;
    let url = null;
    if (
      this.selectedOriginator &&
      this.selectedOriginator.deviceData &&
      this.selectedOriginator.deviceData.capabilities
    ) {
      if (
        this.selectedOriginator.deviceData.capabilities.proxy &&
        this.selectedOriginator.deviceData.capabilities.proxy['local-port']
      ) {
        // if there is no proxy will not have the secondary URL
        port = this.selectedOriginator.deviceData.capabilities['proxy']['local-port'];
      }
      if (
        this.selectedOriginator.deviceData.capabilities.truespeed &&
        this.selectedOriginator.deviceData.capabilities['truespeed']['public-access-ip']
      ) {
        proxyIP = this.selectedOriginator.deviceData.capabilities['truespeed']['public-access-ip'];
      }
      if (port && proxyIP) {
        if (
          serverConfig &&
          serverConfig['vibe-public-access'].protocol &&
          serverConfig['vibe-public-access'].path
        ) {
          url = serverConfig['vibe-public-access'].protocol + '://';
          url += proxyIP + ':' + port;
          if (serverConfig['vibe-public-access'].path.indexOf('/') !== 0) {
            url += '/';
          }
          url += serverConfig['vibe-public-access'].path + shaID;
        }
      }
    }
    return url;
  }

  getLicenseData(): Observable<any> {
    return this.apiService.get$(this.licensePath);
  }

  getServerConfig(): Observable<any> {
    return this.apiService.post$(this.serverConfigPath);
  }
  //eslint-disable-next-line
  setServerConfigData(data: any): void {
    this.serverConfigDetails.next(data);
  }
}
